"use client";
// Cloudinary Configuration
// This file handles image and video uploads to Cloudinary

const CLOUDINARY_CLOUD_NAME = "ft4aubnj";
const CLOUDINARY_UPLOAD_PRESET = "Kamdhenuimg";
const CLOUDINARY_IMAGE_UPLOAD_URL = `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`;
const CLOUDINARY_VIDEO_UPLOAD_URL = `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/video/upload`;

/**
 * Compress image before upload to save storage
 * @param {File} file - Original image file
 * @returns {Promise<Blob>} - Compressed image blob
 */
const compressImage = async (file) => {
    return new Promise((resolve) => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const img = new Image();

        img.onload = () => {
            // Calculate new dimensions (max 1600px, maintains aspect ratio)
            let { width, height } = img;
            const maxSize = 1600;

            if (width > maxSize || height > maxSize) {
                if (width > height) {
                    height = (height / width) * maxSize;
                    width = maxSize;
                } else {
                    width = (width / height) * maxSize;
                    height = maxSize;
                }
            }

            canvas.width = width;
            canvas.height = height;
            ctx.drawImage(img, 0, 0, width, height);

            // Convert to blob with 80% quality (good balance of quality vs size)
            canvas.toBlob(
                (blob) => {
                    console.log(`Compressed: ${(file.size / 1024 / 1024).toFixed(2)} MB → ${(blob.size / 1024).toFixed(0)} KB`);
                    resolve(blob);
                },
                'image/jpeg',
                0.80 // 80% quality - visually identical, 70-90% smaller
            );
        };

        img.src = URL.createObjectURL(file);
    });
};

/**
 * Upload an image to Cloudinary (with compression)
 * @param {File|Blob} file - The image file to upload
 * @returns {Promise<string>} - The secure URL of the uploaded image
 */
export const uploadToCloudinary = async (file) => {
    // Compress image before upload to save storage
    const compressedBlob = await compressImage(file);

    const formData = new FormData();
    formData.append("file", compressedBlob, file.name || 'image.jpg');
    formData.append("upload_preset", CLOUDINARY_UPLOAD_PRESET);

    try {
        const response = await fetch(CLOUDINARY_IMAGE_UPLOAD_URL, {
            method: "POST",
            body: formData,
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error?.message || "Upload failed");
        }

        const data = await response.json();
        return data.secure_url; // Return the HTTPS URL of the uploaded image
    } catch (error) {
        console.error("Cloudinary upload error:", error);
        throw error;
    }
};

/**
 * Upload a video to Cloudinary
 * @param {File} file - The video file to upload
 * @param {Function} onProgress - Optional callback for progress updates
 * @returns {Promise<string>} - The secure URL of the uploaded video
 */
export const uploadVideoToCloudinary = async (file, onProgress) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("upload_preset", CLOUDINARY_UPLOAD_PRESET);
    formData.append("resource_type", "video");

    try {
        // Use XMLHttpRequest for progress tracking
        return new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();

            xhr.upload.addEventListener('progress', (e) => {
                if (e.lengthComputable && onProgress) {
                    const percent = Math.round((e.loaded / e.total) * 100);
                    onProgress(percent);
                }
            });

            xhr.addEventListener('load', () => {
                if (xhr.status >= 200 && xhr.status < 300) {
                    const data = JSON.parse(xhr.responseText);
                    console.log(`Video uploaded: ${(file.size / 1024 / 1024).toFixed(2)} MB`);
                    resolve(data.secure_url);
                } else {
                    const errorData = JSON.parse(xhr.responseText);
                    reject(new Error(errorData.error?.message || "Video upload failed"));
                }
            });

            xhr.addEventListener('error', () => {
                reject(new Error("Video upload failed"));
            });

            xhr.open('POST', CLOUDINARY_VIDEO_UPLOAD_URL);
            xhr.send(formData);
        });
    } catch (error) {
        console.error("Cloudinary video upload error:", error);
        throw error;
    }
};

/**
 * Check if file is a video
 * @param {File} file - File to check
 * @returns {boolean}
 */
export const isVideoFile = (file) => {
    return file.type.startsWith('video/');
};

/**
 * Check if URL is a video
 * @param {string} url - URL to check
 * @returns {boolean}
 */
export const isVideoUrl = (url) => {
    if (!url) return false;
    const videoExtensions = ['.mp4', '.webm', '.mov', '.avi', '.mkv', '.m4v'];
    const lowerUrl = url.toLowerCase();
    return videoExtensions.some(ext => lowerUrl.includes(ext)) || lowerUrl.includes('/video/');
};

/**
 * Upload multiple images to Cloudinary
 * @param {File[]} files - Array of image files to upload
 * @param {Function} onProgress - Optional callback for progress updates
 * @returns {Promise<string[]>} - Array of secure URLs
 */
export const uploadMultipleToCloudinary = async (files, onProgress) => {
    const urls = [];

    for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const url = await uploadToCloudinary(file);
        urls.push(url);

        if (onProgress) {
            onProgress(i + 1, files.length);
        }
    }

    return urls;
};

/**
 * Get optimized Cloudinary URL for frontend display
 * @param {string} url - Original Cloudinary URL
 * @param {object} options - Optimization options
 * @returns {string} - Optimized URL
 */
export const getOptimizedUrl = (url, options = {}) => {
    if (!url || !url.includes('cloudinary.com')) {
        return url; // Return as-is if not a Cloudinary URL
    }

    const {
        width = null,
        height = null,
        quality = 'auto',
        format = 'auto',
        crop = 'fill'
    } = options;

    // Build transformation string
    let transforms = [`q_${quality}`, `f_${format}`];
    if (width) transforms.push(`w_${width}`);
    if (height) transforms.push(`h_${height}`);
    if (width || height) transforms.push(`c_${crop}`);

    const transformStr = transforms.join(',');

    // Insert transformation into URL
    return url.replace('/upload/', `/upload/${transformStr}/`);
};

// Preset optimization functions for common use cases
export const getThumbUrl = (url) => getOptimizedUrl(url, { width: 100, height: 100 });
export const getListUrl = (url) => getOptimizedUrl(url, { width: 400 });
export const getDetailUrl = (url) => getOptimizedUrl(url, { width: 1200 });

export { CLOUDINARY_CLOUD_NAME, CLOUDINARY_UPLOAD_PRESET };
